import { format, parseISO } from "date-fns";
import { durationMinutes, formatHours } from "./duration";
/**
 * Role-rate fallback used when a time entry's service type has no rate set.
 * Keeps the lights on for entries logged before the rate column was populated
 * on msdyn_transactioncategory (custom `crb7a_rate`; unit comes from OOB
 * `msdyn_defaultunit`).
 */
export function rateFor(resourceCategoryId, categories) {
    if (!resourceCategoryId)
        return 0;
    return categories.find((c) => c.bookableresourcecategoryid === resourceCategoryId)?.hourlyRate ?? 0;
}
/**
 * Hint string for the Service-type picker — "$220/hr" or "$2/min". Empty when
 * the service type has no rate (the picker just shows the label then).
 */
export function rateHint(tc) {
    if (typeof tc.rate !== "number" || tc.rate <= 0)
        return "";
    const unit = tc.rateUnit === "Minute" ? "min" : "hr";
    // Whole-dollar amounts read cleaner on the dropdown row; show decimals only
    // when they're meaningful (per-minute rates often are).
    const amount = Number.isInteger(tc.rate) ? `$${tc.rate}` : `$${tc.rate.toFixed(2)}`;
    return `${amount}/${unit}`;
}
/**
 * Revenue for a single time entry, in dollars.
 *
 * Resolution order:
 *  1. Service-type rate (msdyn_transactioncategory → crb7a_rate; unit from
 *     OOB msdyn_defaultunit). Hour unit: hours × rate. Minute unit: minutes × rate.
 *  2. Fallback to the resource-role hourly rate (bookableresourcecategory).
 *  3. Zero if neither is set.
 *
 * Non-chargeable / complimentary service types still produce a number here —
 * the caller (ClientBilling) gates on isBillable() before summing into revenue.
 */
export function lineAmount(entry, txnCategories, resourceCategories) {
    const minutes = durationMinutes(entry);
    const tc = entry.msdyn_transactioncategoryid
        ? txnCategories.find((x) => x.msdyn_transactioncategoryid === entry.msdyn_transactioncategoryid)
        : undefined;
    if (tc && typeof tc.rate === "number" && tc.rate > 0) {
        return tc.rateUnit === "Minute" ? minutes * tc.rate : (minutes / 60) * tc.rate;
    }
    // Fallback: legacy role-based hourly rate.
    return (minutes / 60) * rateFor(entry.resourceCategoryId, resourceCategories);
}
/**
 * Compose the WHMCS-ready line text for an entry. Output shape:
 *   "<Service Type> - <DD/MM/YYYY> - <Description> - <Qty> <Units> @ <Rate>/<Unit>"
 *
 * Description resolves to: Work Order name (WO-regarding) or
 * "Project - Task" / "Project" (project-regarding) or the entry's
 * external/details/internal description (account-regarding).
 *
 * Returns "" when the service type is missing or has no rate — the button
 * stays disabled in that case so the caller doesn't render an empty line.
 */
export function formatBillableLine(entry, tc, refs) {
    if (!tc || typeof tc.rate !== "number" || tc.rate <= 0)
        return "";
    const minutes = durationMinutes(entry);
    const isMin = tc.rateUnit === "Minute";
    const qty = isMin ? minutes : minutes / 60;
    const unitLabel = isMin ? "Minutes" : "Hours";
    const unitSuffix = isMin ? "Min" : "Hour";
    const date = format(parseISO(entry.msdyn_start), "dd/MM/yyyy");
    let desc = "";
    const r = entry.regardingobjectid;
    if (r?.type === "msdyn_workorder") {
        desc = refs.workOrders.find((w) => w.msdyn_workorderid === r.id)?.msdyn_name ?? r.display;
    }
    else if (r?.type === "msdyn_project") {
        const proj = refs.projects.find((p) => p.msdyn_projectid === r.id);
        const task = entry.msdyn_projecttaskid
            ? refs.projectTasks.find((t) => t.msdyn_projecttaskid === entry.msdyn_projecttaskid)
            : undefined;
        desc = proj && task
            ? `${proj.msdyn_subject} - ${task.msdyn_subject}`
            : proj?.msdyn_subject ?? r.display;
    }
    else {
        desc =
            entry.msdyn_externaldescription?.trim() ||
                entry.crb7a_detailsofwork?.trim() ||
                entry.msdyn_description;
    }
    return `${tc.msdyn_description} - ${date} - ${desc} - ${isMin ? qty : formatHours(qty)} ${unitLabel} @ ${tc.rate.toFixed(2)}/${unitSuffix}`;
}
